import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useEffect
} from 'react';
import type {
  SettingsContextProps,
  ThemeMode,
  ThemeLayout,
  ThemeDirection,
  ThemeColorPresets
} from './types';

// ----------------------------------------------------------------------

const defaultSettings: SettingsContextProps = {
  themeMode: 'light',
  themeLayout: 'vertical',
  themeDirection: 'ltr',
  themeStretch: false,
  themeColorPresets: 'default',
  setThemeMode: () => {},
  setThemeLayout: () => {},
  setThemeDirection: () => {},
  setThemeStretch: () => {},
  onToggleMode: () => {},
  onResetSetting: () => {}
};

const SettingsContext = createContext<SettingsContextProps>(defaultSettings);

// ----------------------------------------------------------------------

type SettingsProviderProps = {
  children: ReactNode;
};

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme-mode');
      if (stored === 'dark' || stored === 'light') return stored;
      // Check system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    }
    return 'light';
  });

  const [themeLayout, setThemeLayoutState] = useState<ThemeLayout>('vertical');
  const [themeDirection, setThemeDirectionState] = useState<ThemeDirection>('ltr');
  const [themeStretch, setThemeStretchState] = useState(false);
  const [themeColorPresets] = useState<ThemeColorPresets>('default');

  // Sync theme mode to localStorage and document
  useEffect(() => {
    localStorage.setItem('theme-mode', themeMode);
    document.documentElement.classList.toggle('dark', themeMode === 'dark');
  }, [themeMode]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
  }, []);

  const setThemeLayout = useCallback((layout: ThemeLayout) => {
    setThemeLayoutState(layout);
  }, []);

  const setThemeDirection = useCallback((direction: ThemeDirection) => {
    setThemeDirectionState(direction);
  }, []);

  const setThemeStretch = useCallback((stretch: boolean) => {
    setThemeStretchState(stretch);
  }, []);

  const onToggleMode = useCallback(() => {
    setThemeModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const onResetSetting = useCallback(() => {
    setThemeModeState('light');
    setThemeLayoutState('vertical');
    setThemeDirectionState('ltr');
    setThemeStretchState(false);
  }, []);

  const value = useMemo(
    () => ({
      themeMode,
      themeLayout,
      themeDirection,
      themeStretch,
      themeColorPresets,
      setThemeMode,
      setThemeLayout,
      setThemeDirection,
      setThemeStretch,
      onToggleMode,
      onResetSetting
    }),
    [
      themeMode,
      themeLayout,
      themeDirection,
      themeStretch,
      themeColorPresets,
      setThemeMode,
      setThemeLayout,
      setThemeDirection,
      setThemeStretch,
      onToggleMode,
      onResetSetting
    ]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettingsContext() {
  const context = useContext(SettingsContext);

  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }

  return context;
}
